import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';
import { AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import { RuleTag } from '@/components/RuleTag';
import {
  INJURY_CAUSES,
  TRIAGE_RED_PHYSIOLOGIC,
  TRIAGE_RED_ANATOMIC,
  TRIAGE_YELLOW_MECHANISM,
  TRIAGE_YELLOW_JUDGMENT,
  ALCOHOL_DRUG_INDICATORS,
  type TriageCriterion,
} from '@/lib/nemsis-elements';
import { isSetActive, CONDITIONAL_SETS } from '@/lib/conditional-cascade';
import { MvcSubSet } from './trauma/MvcSubSet';
import { PediatricTraumaSubSet } from './trauma/PediatricTraumaSubSet';
import {
  FallSubSet, AssaultSubSet, BurnSubSet, PenetratingSubSet,
} from './trauma/OtherTraumaSubSets';

/**
 * TraumaTab — EMSPCR Foundation v1.0 §4 (Trauma Set).
 *
 * Renders the conditionally-required trauma elements in one scrollable
 * column:
 *
 *   - eSituation.02  Possible Injury (Yes / No / Unknown)
 *   - eInjury.01     Cause of Injury (multi-select)
 *   - eInjury.03     Red triage criteria (ACS 2021 physiologic + anatomic)
 *   - eInjury.04     Yellow triage criteria (ACS 2021 mechanism + judgment)
 *   - eHistory.17    Alcohol / Drug Use Indicators
 *
 * The tab auto-activates when the conditional cascade engine flags
 * 'trauma' as active (primary impression hits a trauma term or the
 * crew set Possible Injury = Yes). When no trauma conditions are
 * present, the tab still renders — it's the documented-not-applicable
 * path (crew sets Possible Injury = No and moves on).
 */

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

export function TraumaTab({ epcr, onUpdate }: Props) {
  const locked = epcr.locked;
  const traumaActive = isSetActive(epcr, 'trauma');
  const redSelections  = new Set(epcr.trauma_triage_red ?? []);
  const yellowSelections = new Set(epcr.trauma_triage_yellow ?? []);
  const causes = new Set(epcr.injury_causes ?? []);
  const adIndicators = new Set(epcr.alcohol_drug_indicators ?? []);
  const hasRedCriteria = redSelections.size > 0;

  function toggleSetValue(
    field: 'trauma_triage_red' | 'trauma_triage_yellow' | 'injury_causes' | 'alcohol_drug_indicators',
    value: string,
  ) {
    const current = new Set((epcr[field] as string[] | undefined) ?? []);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    onUpdate({ [field]: Array.from(current) } as Partial<EPCR>);
  }

  return (
    <div className="space-y-5">
      {/* ============================================================
          Header + rule-tag row
          ============================================================ */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold uppercase tracking-tight text-slate-100 flex items-center gap-2">
            <ShieldAlert size={18} className="text-amber-400" />
            Trauma Documentation
          </h2>
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mt-0.5">
            NEMSIS Trauma Set · ACS 2021 Field Triage · RIDOH Trauma Registry
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <RuleTag elementId="eInjury.03" />
          <RuleTag elementId="eInjury.04" />
          <RuleTag elementId="eHistory.17" />
        </div>
      </div>

      {/* Trauma Set activation banner */}
      <div
        className={cn(
          'flex items-start gap-2 border px-3 py-2 text-xs leading-relaxed',
          traumaActive
            ? 'border-amber-700 bg-amber-950/30 text-amber-200'
            : 'border-slate-700 bg-slate-900/60 text-slate-400',
        )}
      >
        <Info size={14} className="mt-0.5 shrink-0" />
        <div className="flex-1">
          <span className="font-bold uppercase tracking-widest text-[10px] mr-1">
            {traumaActive ? 'Trauma Set active' : 'Trauma Set inactive'}
          </span>
          <span>
            {traumaActive
              ? CONDITIONAL_SETS.trauma.rationale
              : 'Set "Possible Injury = Yes" below or document a trauma primary impression to activate the full Trauma Set.'}
          </span>
        </div>
      </div>

      {/* ============================================================
          eSituation.02 — Possible Injury
          ============================================================ */}
      <section className="border border-slate-700 bg-slate-900/50">
        <header className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-3 py-1.5">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
            Possible Injury
          </h3>
          <RuleTag elementId="eSituation.02" />
        </header>
        <div className="flex gap-2 p-3">
          {(['yes', 'no', 'unknown'] as const).map((v) => (
            <button
              key={v}
              type="button"
              disabled={locked}
              onClick={() => onUpdate({ possible_injury: v })}
              className={cn(
                'flex-1 border px-3 py-2 text-xs font-bold uppercase tracking-widest transition-colors',
                epcr.possible_injury === v
                  ? v === 'yes'
                    ? 'bg-amber-500 text-black border-amber-400'
                    : 'bg-slate-700 text-slate-100 border-slate-500'
                  : 'bg-slate-800 text-slate-400 border-slate-600 hover:bg-slate-700',
              )}
            >
              {v}
            </button>
          ))}
        </div>
      </section>

      {/* ============================================================
          eInjury.01 — Cause of Injury (shown when Possible Injury = Yes)
          ============================================================ */}
      {(traumaActive || epcr.possible_injury === 'yes') && (
        <section className="border border-slate-700 bg-slate-900/50">
          <header className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-3 py-1.5">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
              Cause of Injury
            </h3>
            <RuleTag elementId="eInjury.01" />
          </header>
          <div className="p-3 grid gap-1.5 sm:grid-cols-2 md:grid-cols-3">
            {INJURY_CAUSES.map((cause) => (
              <Checkbox
                key={cause}
                label={cause}
                checked={causes.has(cause)}
                disabled={locked}
                onChange={() => toggleSetValue('injury_causes', cause)}
              />
            ))}
          </div>
        </section>
      )}

      {/* ============================================================
          eInjury.03 — Red (high-risk) triage criteria
          ============================================================ */}
      {(traumaActive || epcr.possible_injury === 'yes') && (
        <section className="border border-red-700/60 bg-red-950/10">
          <header className="flex items-center justify-between border-b border-red-800/40 bg-red-950/30 px-3 py-1.5">
            <div className="flex items-center gap-2">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-red-300">
                Red Criteria — High Risk
              </h3>
              <span className="text-[9px] font-mono uppercase tracking-widest text-red-400/80">
                any yes → highest-level trauma center
              </span>
            </div>
            <RuleTag
              sources={[
                { standard: 'NEMSIS', element: 'eInjury.03', usage: 'Required' },
                { standard: 'ACS-2021', rationale: '2021 National Field Triage Guideline — RED criteria' },
              ]}
            />
          </header>
          <div className="p-3 space-y-4">
            <CriteriaGrid
              title="Physiologic"
              criteria={TRIAGE_RED_PHYSIOLOGIC}
              selected={redSelections}
              disabled={locked}
              onToggle={(c) => toggleSetValue('trauma_triage_red', c.code)}
            />
            <CriteriaGrid
              title="Anatomic"
              criteria={TRIAGE_RED_ANATOMIC}
              selected={redSelections}
              disabled={locked}
              onToggle={(c) => toggleSetValue('trauma_triage_red', c.code)}
            />
          </div>
          {hasRedCriteria && (
            <div className="flex items-start gap-2 border-t border-red-800/40 bg-red-950/60 px-3 py-2">
              <AlertTriangle size={14} className="mt-0.5 text-red-300 shrink-0" />
              <div className="text-xs text-red-200 leading-snug">
                <span className="font-bold">Trauma Alert</span> — one or more Red criteria selected.
                ACS 2021 recommends transport to the highest-level trauma center. Confirm destination
                on the Disposition tab.
              </div>
            </div>
          )}
        </section>
      )}

      {/* ============================================================
          eInjury.04 — Yellow (moderate-risk) triage criteria
          ============================================================ */}
      {(traumaActive || epcr.possible_injury === 'yes') && (
        <section className="border border-amber-700/60 bg-amber-950/10">
          <header className="flex items-center justify-between border-b border-amber-800/40 bg-amber-950/30 px-3 py-1.5">
            <div className="flex items-center gap-2">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-amber-300">
                Yellow Criteria — Moderate Risk
              </h3>
              <span className="text-[9px] font-mono uppercase tracking-widest text-amber-400/80">
                any yes (no red) → any trauma center
              </span>
            </div>
            <RuleTag
              sources={[
                { standard: 'NEMSIS', element: 'eInjury.04', usage: 'Required' },
                { standard: 'ACS-2021', rationale: '2021 National Field Triage Guideline — YELLOW criteria' },
              ]}
            />
          </header>
          <div className="p-3 space-y-4">
            <CriteriaGrid
              title="Mechanism of Injury"
              criteria={TRIAGE_YELLOW_MECHANISM}
              selected={yellowSelections}
              disabled={locked}
              onToggle={(c) => toggleSetValue('trauma_triage_yellow', c.code)}
            />
            <CriteriaGrid
              title="EMS Judgment / Special Considerations"
              criteria={TRIAGE_YELLOW_JUDGMENT}
              selected={yellowSelections}
              disabled={locked}
              onToggle={(c) => toggleSetValue('trauma_triage_yellow', c.code)}
            />
          </div>
        </section>
      )}

      {/* ============================================================
          MVC Sub-Set — DOT data capture (§4.5)
          ============================================================ */}
      {isSetActive(epcr, 'trauma_mvc') && (
        <MvcSubSet epcr={epcr} onUpdate={onUpdate} />
      )}

      {/* Fall / Assault / Burn / Penetrating sub-sets (§4) — each
          self-gates on its own cascade trigger so only mechanism-
          specific blocks render. */}
      {isSetActive(epcr, 'trauma_fall') && (
        <FallSubSet epcr={epcr} onUpdate={onUpdate} />
      )}
      {isSetActive(epcr, 'trauma_assault') && (
        <AssaultSubSet epcr={epcr} onUpdate={onUpdate} />
      )}
      {isSetActive(epcr, 'trauma_burn') && (
        <BurnSubSet epcr={epcr} onUpdate={onUpdate} />
      )}
      {isSetActive(epcr, 'trauma_penetrating') && (
        <PenetratingSubSet epcr={epcr} onUpdate={onUpdate} />
      )}

      {/* ============================================================
          Pediatric Trauma Sub-Set (§4.6)
          ============================================================ */}
      {isSetActive(epcr, 'trauma_pediatric') && (
        <PediatricTraumaSubSet epcr={epcr} onUpdate={onUpdate} />
      )}

      {/* ============================================================
          eHistory.17 — Alcohol / Drug Use Indicators
          ============================================================ */}
      <section className="border border-slate-700 bg-slate-900/50">
        <header className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-3 py-1.5">
          <div>
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
              Alcohol / Drug Use Indicators
            </h3>
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
              One question satisfies NEMSIS + RIDOH opioid surveillance + CMS medical necessity
            </p>
          </div>
          <RuleTag elementId="eHistory.17" />
        </header>
        <div className="p-3 space-y-3">
          <IndicatorGroup
            title="Alcohol"
            items={ALCOHOL_DRUG_INDICATORS.alcohol}
            selected={adIndicators}
            disabled={locked}
            onToggle={(v) => toggleSetValue('alcohol_drug_indicators', v)}
          />
          <IndicatorGroup
            title="Drug"
            items={ALCOHOL_DRUG_INDICATORS.drug}
            selected={adIndicators}
            disabled={locked}
            onToggle={(v) => toggleSetValue('alcohol_drug_indicators', v)}
          />
          <IndicatorGroup
            title="Behavioral / Physical Signs"
            items={ALCOHOL_DRUG_INDICATORS.behavioral}
            selected={adIndicators}
            disabled={locked}
            onToggle={(v) => toggleSetValue('alcohol_drug_indicators', v)}
          />
          <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-800">
            <Checkbox
              label={ALCOHOL_DRUG_INDICATORS.negative}
              checked={adIndicators.has(ALCOHOL_DRUG_INDICATORS.negative)}
              disabled={locked}
              onChange={() => toggleSetValue('alcohol_drug_indicators', ALCOHOL_DRUG_INDICATORS.negative)}
              tone="success"
            />
            <Checkbox
              label={ALCOHOL_DRUG_INDICATORS.unassessed}
              checked={adIndicators.has(ALCOHOL_DRUG_INDICATORS.unassessed)}
              disabled={locked}
              onChange={() => toggleSetValue('alcohol_drug_indicators', ALCOHOL_DRUG_INDICATORS.unassessed)}
              tone="muted"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

// ------------------------------------------------------------------
function CriteriaGrid({
  title, criteria, selected, disabled, onToggle,
}: {
  title: string;
  criteria: TriageCriterion[];
  selected: Set<string>;
  disabled: boolean;
  onToggle: (c: TriageCriterion) => void;
}) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
        {title}
      </div>
      <div className="grid gap-1.5 md:grid-cols-2">
        {criteria.map((c) => (
          <Checkbox
            key={c.code}
            label={c.label}
            code={c.code}
            checked={selected.has(c.code)}
            disabled={disabled}
            onChange={() => onToggle(c)}
          />
        ))}
      </div>
    </div>
  );
}

function IndicatorGroup({
  title, items, selected, disabled, onToggle,
}: {
  title: string;
  items: readonly string[];
  selected: Set<string>;
  disabled: boolean;
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
        {title}
      </div>
      <div className="grid gap-1.5 md:grid-cols-2">
        {items.map((v) => (
          <Checkbox
            key={v}
            label={v}
            checked={selected.has(v)}
            disabled={disabled}
            onChange={() => onToggle(v)}
          />
        ))}
      </div>
    </div>
  );
}

function Checkbox({
  label, code, checked, disabled, onChange, tone = 'default',
}: {
  label: string;
  code?: string;
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  tone?: 'default' | 'success' | 'muted';
}) {
  const toneClasses = tone === 'success'
    ? 'border-emerald-700 bg-emerald-950/50 text-emerald-200'
    : tone === 'muted'
      ? 'border-slate-700 bg-slate-900 text-slate-400'
      : 'border-slate-700 bg-slate-900/70 text-slate-200 hover:bg-slate-800';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'flex items-start gap-2 border px-2.5 py-1.5 text-left text-xs leading-snug transition-colors',
        checked
          ? 'border-amber-500 bg-amber-950/40 text-amber-100'
          : toneClasses,
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center border text-[9px] font-black',
          checked
            ? 'border-amber-400 bg-amber-500 text-black'
            : 'border-slate-500 bg-slate-800',
        )}
      >
        {checked ? '✓' : ''}
      </span>
      <span className="flex-1">
        {label}
        {code && (
          <span className="ml-1 font-mono text-[9px] text-slate-500">· {code}</span>
        )}
      </span>
    </button>
  );
}
